import { useTranslation } from "react-i18next";
import { memo, useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";

import {
  extractError,
  getCardTypeFromTsysData,
  getDefaultInputValue,
  renderConditionalProp,
} from "utility";

import { useInput, UseInput, useRequest, useAddNotification } from "hooks";
import { useRequestDispatch } from "store/requestHooks";
import { useRootDispatch, useRootSelector } from "store/hooks";

import * as requestUrl from "../../../../../constants/request";
import { scopes } from "constants/idp-scopes";
import { PaymentActionType } from "../AddPaymentMethods";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";

import {
  SavePaymentMethod,
  savePaymentMethod,
} from "store/paymentMethods/paymentMethods-action";
import { updateSlide } from "store/common/common-slice";
import {
  selectAllPaymentMethods,
  selectPaymentMethodById,
} from "store/paymentMethods/paymentMethods-slice";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import { selectedAccountInfo } from "store/account/account-selector";

import classes from "./CreditCard.module.scss";
import { Loader } from "rsuite";
import { hasAccountPermission } from "utility/account";
import { AccountMatrix } from "store/account/account-state";
import { useEffectDebounce } from "hooks/useEffectDebounce";
import { zipRegex } from "utility/regex";
import { TsysConfiguration } from "store/config/ewallet-configuration";

export interface CreditCardProps {
  editMode?: boolean;
  action?: PaymentActionType;
  buttonClick?: MakePaymentClick;
  updateButtonClick?: (
    updateFn: (prev: MakePaymentClick) => MakePaymentClick,
  ) => void;
  paymentHandler?: Function;
}

export const CreditCard: React.FC<CreditCardProps> = memo(
  ({
    editMode,
    action = PaymentActionType.SAVE,
    buttonClick,
    updateButtonClick,
    paymentHandler,
  }) => {
    const [disabled, setDisabled] = useState(false);
    const { t } = useTranslation(["common", "payment"]);
    const { request } = useRequest(scopes.users.writeUser);
    const { client } = useRootSelector((state) => state.config);
    const [tsysData, setTsysData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isTokenRequested, setIsTokenRequested] = useState(false);
    const accountInfo = useRootSelector(selectedAccountInfo);
    const addNotification = useAddNotification();

    useEffect(() => {
      if (
        !hasAccountPermission(
          accountInfo?.clientAttributes,
          AccountMatrix.EnableCard,
        )
      ) {
        setDisabled(true);
      }
    }, [accountInfo, setDisabled]);

    const card = useRootSelector((state) => {
      const id = state.common.pageDetail?.data?.id;
      if (id) {
        return selectPaymentMethodById(state, id);
      }
      return null;
    });

    const existingPaymentMethods = useRootSelector((state) =>
      selectAllPaymentMethods(state),
    );

    const ack: UseInput = useInput(
      getDefaultInputValue(PaymentActionType.PAY ? false : card?.ack),
    );

    const dispatch = useRootDispatch();
    const requestDispatch = useRequestDispatch();

    const nickName: UseInput = useInput(
      getDefaultInputValue(card?.paymentMethodName, ""),
    );

    // Address Data
    const addressLine1: UseInput = useInput(
      getDefaultInputValue(card?.address?.line1, ""),
    );
    const addressLine2: UseInput = useInput(
      getDefaultInputValue(card?.address?.line2, ""),
    );
    const zipCode: UseInput = useInput();
    const cardHolderName: UseInput = useInput();

    const addressValidation = {
      line1MinLength: "2",
      line1MaxLength: "50",
      line2MaxLength: "40",
      zipMinLength: "5",
      zipMaxLength: "10",
      cardHolderNameMinLength: "100",
    };

    // Interfaces
    interface DynamicScript {
      src: string;
      async: boolean;
      referrerPolicy?: string;
    }

    useEffect(() => {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            const cardNumberInput = document.getElementById("tsep-cardNum");
            if (cardNumberInput) {
              const cardNumberPlaceholder =
                t("payment_next:card_number_label") + "*";
              cardNumberInput.setAttribute(
                "placeholder",
                cardNumberPlaceholder,
              );
            }

            const expiryDateInput = document.getElementById("tsep-datepicker");
            if (expiryDateInput) {
              const expiryDatePlaceholder =
                t("payment_next:expiry_label") + "*";
              expiryDateInput.setAttribute(
                "placeholder",
                expiryDatePlaceholder,
              );
            }

            const cvvInput = document.getElementById("tsep-cvv2");
            if (cvvInput) {
              const cvvPlaceholder =
                t("payment_next:cvv_label", { defaultValue: "CVV" }) + "*";
              cvvInput.setAttribute("placeholder", cvvPlaceholder);
            }

            if (cardNumberInput && expiryDateInput && cvvInput) {
              observer.disconnect();
            }
          }
        }
      });

      const targetNodes = [
        document.getElementById("tsep-cardNumDiv"),
        document.getElementById("tsep-datepickerDiv"),
        document.getElementById("tsep-cvv2Div"),
      ];

      targetNodes.forEach((node) => {
        if (node) {
          observer.observe(node, { childList: true, subtree: true });
        }
      });

      return () => {
        observer.disconnect();
      };
    }, [t]);

    //#region Functions

    /**
     * This handler handles the logic when action is PaymentActionType.SAVE & PaymentActionType.PAY
     */
    const submithandler = () => {
      let formIsValid = true;

      formIsValid = triggerFormValidation();
      if (formIsValid) {
        savePaymentMethodHandler();
      }
    };

    const validateFieldOnBlur = useCallback(
      (input: HTMLElement | null, placeholder: string) => {
        if (input) {
          const value = (input as HTMLInputElement).value || "";

          const fieldName = input.id.startsWith("tsep-")
            ? input.id.slice(5)
            : input.id;
          const customErrorId = `error_tsep-${fieldName}`;
          const tsysErrorId = `error_${input.id}`;

          const existingCustomErrorElement =
            document.getElementById(customErrorId);
          const existingTsysErrorElement = document.getElementById(tsysErrorId);

          if (!value) {
            if (!existingCustomErrorElement && !existingTsysErrorElement) {
              const span = document.createElement("span");
              span.innerText = t("common:field_not_empty");
              span.id = customErrorId;
              input.parentNode?.appendChild(span);
            }
          } else {
            if (existingCustomErrorElement && !existingTsysErrorElement) {
              existingCustomErrorElement.remove();
            }
          }
        }
      },
      [t],
    );

    useEffect(() => {
      const observer = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
          if (mutation.type === "childList") {
            // Check if each specific input field is now in the DOM
            const cardNumberInput = document.getElementById("tsep-cardNum");
            if (cardNumberInput) {
              cardNumberInput.addEventListener("blur", () =>
                validateFieldOnBlur(
                  cardNumberInput,
                  t("payment_next:card_number_label"),
                ),
              );
            }

            const expiryDateInput = document.getElementById("tsep-datepicker");
            if (expiryDateInput) {
              expiryDateInput.addEventListener("blur", () =>
                validateFieldOnBlur(
                  expiryDateInput,
                  t("payment_next:expiry_label_message"),
                ),
              );
            }

            const cvvInput = document.getElementById("tsep-cvv2");
            if (cvvInput) {
              cvvInput.addEventListener("blur", () =>
                validateFieldOnBlur(
                  cvvInput,
                  t("payment_next:cvv_label", { defaultValue: "CVV" }),
                ),
              );
            }
          }
        });
      });

      // Observe the DOM for dynamically added nodes
      observer.observe(document.body, { childList: true, subtree: true });

      // Cleanup observer and event listeners on unmount
      return () => {
        observer.disconnect();
        ["tsep-cardNum", "tsep-datepicker", "tsep-cvv2"].forEach((id) => {
          const input = document.getElementById(id);
          if (input) {
            input.removeEventListener("blur", () =>
              validateFieldOnBlur(input, ""),
            );
          }
        });
      };
    }, [t, validateFieldOnBlur]);

    const triggerFormValidation = () => {
      let isFormValid = true;
      const fieldsToValidate = [
        {
          inputId: "cardHolderName",
          fieldName: "cardHolderName",
          placeholder: t("payment_next:name_label"),
          triggerBlur: true,
        },
        {
          inputId: "tsep-cardNum",
          fieldName: "cardNum",
          placeholder: t("payment_next:card_number_label"),
        },
        {
          inputId: "tsep-datepicker",
          fieldName: "datepicker",
          placeholder: t("payment_next:expiry_label_message"),
        },
        {
          inputId: "tsep-cvv2",
          fieldName: "cvv2",
          placeholder: t("payment_next:cvv_label", { defaultValue: "CVV" }),
        },
        {
          inputId: "zipcode",
          fieldName: "zipcode",
          placeholder: t("payment_next:zip_code"),
          triggerBlur: true,
        },
        {
          inputId: "addressLine1",
          fieldName: "addressLine1",
          placeholder: "Address Line 1",
          triggerBlur: true,
        },
        {
          inputId: "nickname",
          fieldName: "nickname",
          placeholder: "nickname",
          triggerBlur: true,
        },
      ];

      fieldsToValidate.forEach(
        ({ inputId, fieldName, placeholder, triggerBlur }) => {
          const input = document.getElementById(
            inputId,
          ) as HTMLInputElement | null;
          const value = input?.value || "";
          const customErrorId = `error_tsep-${fieldName}`;
          const tsysErrorId = `error_${inputId}`;

          const existingCustomErrorElement =
            document.getElementById(customErrorId);
          const existingTsysErrorElement = document.getElementById(tsysErrorId);

          if (triggerBlur) {
            input?.focus();
            input?.blur();
          } else {
            if (!value && !existingTsysErrorElement) {
              if (!existingCustomErrorElement) {
                const span = document.createElement("span");
                span.innerText = t("common:field_not_empty");
                span.id = customErrorId;
                input?.parentNode?.appendChild(span);
              }
              input?.focus();
              input?.blur();
              isFormValid = false;
            }
          }
        },
      );

      return isFormValid;
    };

    /*
      Check form validity
    */
    const isValidForm = () => {
      let valid = true;

      valid = triggerFormValidation();

      if (!addressLine1.ref.current.checkValidity()) {
        addressLine1.ref.current.reportValidity();
        valid = false;
      }

      if (
        !nickName.value &&
        (action === PaymentActionType.SAVE ||
          (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value))
      ) {
        nickName.ref.current.reportValidity();
        valid = false;
      }
      return valid;
    };

    /**
     * Call this from the Parent component to get the Credit Card form data
     * The parent component decides what to do with this data.
     */
    const getCreditCardFormData = useCallback(() => {
      if (tsysData) {
        const cvvInput = document.getElementById("tsep-cvv2") as
          | HTMLInputElement
          | null;
        const normalizedCvv2 =
          cvvInput?.value?.replace(/\D/g, "").slice(0, 4) || tsysData.cvv2 || "";
        const formData: PaymentMethod = {
          cardHolderName: cardHolderName.value,
          tokenizedCardData: tsysData.tsepToken,
          paymentMethodName: nickName.value,
          ack: ack.value,
          expirationDate: tsysData.expirationDate?.replace(/\//g, ""),
          cvv2: normalizedCvv2,
          paymentMethodType: 2,
          address: {
            line1: addressLine1.value,
            line2: addressLine2.value,
            zip: zipCode.value,
          },
          cardType: getCardTypeFromTsysData(tsysData.cardType),
          lastFour: tsysData.maskedCardNumber,
        };
        return formData;
      } else {
        return null;
      }
    }, [
      ack,
      nickName,
      tsysData,
      addressLine1,
      addressLine2,
      zipCode,
      cardHolderName,
    ]);

    const resolveScript = (script: DynamicScript): void => {
      const s = document.createElement("script");
      s.setAttribute("id", "tsep-source");
      s.setAttribute("src", script.src);
      s.async = script.async;
      if (script.referrerPolicy) {
        s.referrerPolicy = script.referrerPolicy;
      }
      document.head.appendChild(s);
      s.onload = () => {
        //When the external script is done executing this is executed
        setLoading(false);
      };
    };

    const tsepFormValidation = (event: any, status: string) => {
      if (status === "SUCCESS") {
        document.getElementById(`error_${event.detail.fieldName}`)?.remove();
      } else {
        const input = document.getElementById(event.detail.fieldName);
        if (!document.getElementById(`error_${event.detail.fieldName}`)) {
          const span = document.createElement("span");
          span.innerText = event.detail.message;
          span.id = `error_${event.detail.fieldName}`;
          input?.parentNode?.insertBefore(span, input.nextSibling);
        } else {
          const span = document.getElementById(
            `error_${event.detail.fieldName}`,
          );
          span!.innerText = event.detail.message;
        }
      }
    };
    //#endregion

    const saveTSYSDataToLocalState = useCallback(
      async (data: any) => {
        if (data.detail.status === "PASS") {
          setTsysData(data.detail);
        }
      },
      [setTsysData],
    );

    const formatExpiryDate = (expirationDate: string): string | null => {
      if (/^\d{2}\/\d{4}$/.test(expirationDate)) {
        const month = expirationDate.slice(0, 2);
        const year = expirationDate.slice(5, 7);
        return `${month}/${year}`;
      }
      if (/^\d{6}$/.test(expirationDate)) {
        const month = expirationDate.slice(0, 2);
        const year = expirationDate.slice(4, 6);
        return `${month}/${year}`;
      }

      return null;
    };

    /**
     * Saves the payment method
     */
    const savePaymentMethodHandler = () => {
      const formData: PaymentMethod | null = getCreditCardFormData();

      if (formData) {
        const formatedExpirationDate = formatExpiryDate(
          formData.expirationDate || "",
        );
        const isDuplicate = existingPaymentMethods.some(
          (method) =>
            method.expirationDate === formatedExpirationDate &&
            method.cardType === formData.cardType &&
            method.tokenizedCardData === formData.tokenizedCardData,
        );
        const isNickNameDuplicate = existingPaymentMethods.some(
          (method) => method.paymentMethodName === formData.paymentMethodName,
        );

        if (isDuplicate) {
          addNotification({
            translatedMessages: [t("common:card_already_exists")],
            type: "ERROR",
            closable: true,
          });
          return;
        }

        if (isNickNameDuplicate) {
          addNotification({
            translatedMessages: [t("common:nickname_exists")],
            type: "ERROR",
            closable: true,
          });
          return;
        }

        formData.accountType = accountInfo?.accountType;

        requestDispatch<SavePaymentMethod, PaymentMethod>(
          savePaymentMethod,
          formData,
        )
          .unwrap()
          .then((res: PaymentMethod) => {
            if (buttonClick?.count) {
              if (paymentHandler) paymentHandler(res);
            } else {
              dispatch(updateSlide({ id: "Confirmation" }));
            }
          })
          .catch((err: AxiosError) => {
            const errCode = extractError(err);
            addNotification({
              messages: errCode,
              type: "ERROR",
              closable: true,
            });
          });
      }
    };

    //#region Effects
    useEffect(() => {
      const handleTokenEvent = (e: any) => {
        const payload = { ...e.detail };
        const isTokenSessionError = payload.responseCode === "TSEP001";

        const raiseEvent = new CustomEvent("TSYSCardValidationEvent", {
          detail: payload,
          bubbles: true,
          composed: true,
        });
        document.dispatchEvent(raiseEvent);

        if (isTokenSessionError) {
          clearTsepFields();
          setIsTokenRequested(false);
        }
      };

      const clearTsepFields = () => {
        const fieldIds = ["tsep-cardNum", "tsep-datepicker", "tsep-cvv2"];

        fieldIds.forEach((id) => {
          const field = document.getElementById(id) as HTMLInputElement;
          if (field) {
            field.parentNode?.removeChild(field);
          }
        });
      };

      const getTsysConfig = async () => {
        let tsysConfigData: TsysConfiguration | null = null;
        request(requestUrl.FT_TSYS_CONFIG, {
          method: "GET",
          params: {
            // This is the clientId (int) for most config requests you can use clientId, idpId, or customerKey.
            // This endpoint only supports clientId.
            clientId: client.clientId,
            accountType: accountInfo?.accountType,
          },
        })
          .then((res: any) => {
            tsysConfigData = res.data;

            if (
              tsysConfigData !== null &&
              requestUrl.FT_TSYS_SERVICE_URL !== undefined
            ) {
              const src =
                requestUrl.FT_TSYS_SERVICE_URL +
                tsysConfigData?.deviceID +
                "?" +
                tsysConfigData?.manifest;
              resolveScript({
                src,
                async: false,
                referrerPolicy: "strict-origin-when-cross-origin",
              });
            } else {
              throw new Error("Can't resolve TSYS script");
            }
          })
          .catch((e: any) => {
            const errCode = extractError(e, "card_form-bad-load");
            addNotification({
              messages: errCode,
              type: "ERROR",
              closable: true,
            });
            // show the error in place of loader
            setError(true);
          })
          .finally(() => {
            setIsTokenRequested(true);
          });
      };

      const handleFieldValidation = (e: any, result: "FAIL" | "SUCCESS") => {
        tsepFormValidation(e, result);
      };

      document.addEventListener("TSYSTokenEvent", handleTokenEvent);
      document.addEventListener("TSYSFieldValidationError", (e) =>
        handleFieldValidation(e, "FAIL"),
      );
      document.addEventListener("TSYSFieldValidationSuccess", (e) =>
        handleFieldValidation(e, "SUCCESS"),
      );

      if (isTokenRequested === false) {
        getTsysConfig();
      }

      return () => {
        document.removeEventListener("TSYSTokenEvent", handleTokenEvent);
        document.removeEventListener("TSYSFieldValidationError", (e) =>
          handleFieldValidation(e, "FAIL"),
        );
        document.removeEventListener("TSYSFieldValidationSuccess", (e) =>
          handleFieldValidation(e, "SUCCESS"),
        );
        if (document) {
          const element = document.getElementById("tsep-source");
          element?.parentNode?.removeChild(element);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTokenRequested]);

    useEffect(() => {
      if (document) {
        const script = document.createElement("script");
        script.setAttribute("id", "tsep-handler");
        script.setAttribute("src", "/scripts/tsep.js");
        script.type = "text/javascript";
        const s = document.body.firstChild;
        s?.parentNode?.insertBefore(script, s);
      }
      return () => {
        if (document) {
          const element = document.getElementById("tsep-handler");
          element?.parentNode?.removeChild(element);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * This event is raised from the UI library, it constructs the tsys data with some additional information
     * and raises this event, which is captured here.
     */
    useEffect(() => {
      if (document) {
        document.addEventListener(
          "TSYSCardValidationEvent",
          saveTSYSDataToLocalState,
        );
      }
      return () => {
        if (document) {
          document.removeEventListener(
            "TSYSCardValidationEvent",
            saveTSYSDataToLocalState,
          );
        }
      };
    }, [saveTSYSDataToLocalState]);

    const resetButtonClick = () => {
      if (updateButtonClick) {
        updateButtonClick((prev) => ({
          ...prev,
          count: 0,
        }));
      }
    };

    useEffectDebounce(
      () => {
        if (buttonClick?.count) {
          if (!isValidForm()) {
            resetButtonClick();
            return;
          }
          const cardData = getCreditCardFormData();
          if (cardData) {
            if (cardData.ack) {
              savePaymentMethodHandler();
            } else {
              if (paymentHandler) paymentHandler(cardData);
            }
          } else {
            resetButtonClick();
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [buttonClick?.count, tsysData, updateButtonClick],
      1000,
    );

    useEffect(() => {
      if (buttonClick?.scheduleFlag) {
        ack.setValue(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buttonClick?.scheduleFlag]);

    // Added for making a tsys request to get tsep token for cards.
    // This should only add on a meta tag while the component is mounted.
    useEffect(() => {
      if (document) {
        const policy = document.createElement("meta");
        policy.setAttribute("id", "tsys-meta");
        policy.setAttribute("name", "referrer");
        policy.setAttribute("content", "origin");
        document.head.appendChild(policy);
      }
      return () => {
        if (document) {
          const element = document.getElementById("tsys-meta");
          element?.parentNode?.removeChild(element);
        }
      };
    }, []);

    return (
      <div
        className={[
          classes.ModuleWrapper,
          action === PaymentActionType.PAY_LATER
            ? classes.Container_Padding
            : "",
        ].join(" ")}
      >
        <div className={classes.AccountDetail}>
          {action !== PaymentActionType.PAY_LATER &&
          action !== PaymentActionType.SAVE_AND_PAY_LATER ? (
            <p className={classes.AccountDetailHeading}>
              {editMode
                ? card?.paymentMethodName
                  ? card?.paymentMethodName
                  : card?.cardHolderName
                : t("payment_next:card_detail")}
            </p>
          ) : null}
          <div>
            {loading ? (
              <div className={classes.Container_Centered}>
                {error ? t("common:card_form-bad-load") : <Loader />}
              </div>
            ) : null}
            <div
              className={
                loading ? classes.Container_hidden : classes.Container_visible
              }
            >
              <div className={classes.payment}>
                <form method="POST" id="payment-form">
                  <fieldset disabled={disabled}>
                    <div>
                      <label htmlFor="cardHolderName">
                        {t("payment_next:name_label")}*
                      </label>
                      <ft-textfield
                        id="cardHolderName"
                        data-testid="add-card-payment-method-card-holder-name"
                        class={classes.Container_visible}
                        placeholder={t("payment_next:name_label") + "*"}
                        required={true}
                        maxLength={addressValidation.cardHolderNameMinLength}
                        outlined
                        ref={cardHolderName.ref}
                        validationMessage={t("common:field_not_empty")}
                        onInput={cardHolderName.valueChangeHandler}
                        value={cardHolderName.value}
                        onBlur={cardHolderName.inputBlurHandler}
                        icon=""
                        disabled={disabled ? disabled : null}
                        extraProp={{
                          required: true,
                        }}
                      ></ft-textfield>
                    </div>
                    <div
                      id="tsep-cardNumDiv"
                      data-testid="add-card-payment-method-card-number-container"
                      data-auto-formatting="Y"
                      data-validate-cc="Y"
                      data-detect-card-type="Y"
                      className={classes.payment}
                    >
                      <label id="tsep-label">
                        {t("payment_next:card_number_label")}*
                      </label>
                    </div>
                    <div
                      id="tsep-datepickerDiv"
                      data-testid="add-card-payment-method-expiry-container"
                      data-validate-expiry-date="Y"
                      className={classes.payment}
                    >
                      <label id="tsep-label">
                        {t("payment_next:expiry_label")}*
                      </label>
                    </div>
                    <div
                      id="tsep-cvv2Div"
                      data-testid="add-card-payment-method-cvv-container"
                      data-validate-cvv="Y"
                      className={classes.payment}
                    >
                      <label id="tsep-label">
                        {t("payment_next:cvv_label", { defaultValue: "CVV" })}*
                      </label>
                    </div>
                    <div>
                      <label htmlFor="addressLine1">
                        {t("payment_next:card_address-line1")}*
                      </label>
                      <ft-textfield
                        id="addressLine1"
                        data-testid="add-card-payment-method-address-line-1"
                        class={classes.Container_visible}
                        placeholder={t("payment_next:card_address-line1") + "*"}
                        required
                        minLength={addressValidation.line1MinLength}
                        maxLength={addressValidation.line1MaxLength}
                        outlined
                        ref={addressLine1.ref}
                        validationMessage={t("common:validation_length", {
                          min: addressValidation.line1MinLength,
                          max: addressValidation.line1MaxLength,
                        })}
                        onInput={addressLine1.valueChangeHandler}
                        value={addressLine1.value}
                        onBlur={addressLine1.inputBlurHandler}
                        icon=""
                        disabled={disabled ? disabled : null}
                      ></ft-textfield>
                    </div>
                    <div>
                      <label htmlFor="addressLine2">
                        {t("payment_next:card_address-line2")}
                      </label>
                      <ft-textfield
                        id="addressLine2"
                        data-testid="add-card-payment-method-address-line-2"
                        class={classes.Container_visible}
                        placeholder={t("payment_next:card_address-line2")}
                        outlined
                        maxLength={addressValidation.line2MaxLength}
                        ref={addressLine2.ref}
                        validationMessage={t("common:field_not_empty")}
                        onInput={addressLine2.valueChangeHandler}
                        value={addressLine2.value}
                        onBlur={addressLine2.inputBlurHandler}
                        icon=""
                        disabled={disabled ? disabled : null}
                      ></ft-textfield>
                    </div>
                    <div>
                      <label htmlFor="zipcode">
                        {t("payment_next:zip_code")}*
                      </label>
                      <ft-textfield
                        id="zipcode"
                        data-testid="add-card-payment-method-zip-code"
                        class={classes.Container_visible}
                        placeholder={t("payment_next:zip_code") + "*"}
                        outlined
                        required={true}
                        maxLength={addressValidation.zipMaxLength}
                        minLength={addressValidation.zipMinLength}
                        ref={zipCode.ref}
                        validationMessage={t("common:zipcode_validation")}
                        onInput={zipCode.valueChangeHandler}
                        value={zipCode.value}
                        onBlur={zipCode.inputBlurHandler}
                        icon=""
                        disabled={disabled ? disabled : null}
                        pattern={zipRegex}
                      ></ft-textfield>
                    </div>
                  </fieldset>
                </form>
              </div>

              {action === PaymentActionType.SAVE ||
              (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value) ? (
                <div>
                  <label htmlFor="nickname">
                    {t("payment_next:card_nickname")}*
                  </label>
                  <ft-textfield
                    id="nickname"
                    data-testid="add-card-payment-method-nickname"
                    class={classes.Container_visible}
                    placeholder={t("payment_next:card_nickname") + "*"}
                    maxLength={50}
                    outlined
                    required
                    ref={nickName.ref}
                    validationMessage={t("common:field_not_empty")}
                    onInput={nickName.valueChangeHandler}
                    value={nickName.value}
                    onBlur={nickName.inputBlurHandler}
                    icon=""
                    disabled={disabled ? disabled : null}
                  ></ft-textfield>
                </div>
              ) : null}
            </div>
          </div>
          <div className={classes.FormControl}>
            {editMode ? null : (
              <>
                {action === PaymentActionType.PAY ? null : (
                  <ft-formfield
                    class={classes.Tnc}
                    label={
                      action === PaymentActionType.SAVE
                        ? t("payment_next:card_review")
                        : t("payment_next:save_method")
                    }
                  >
                    <ft-checkbox
                      data-testid="add-card-payment-method-review-and-tnc"
                      {...renderConditionalProp(ack.value, "checked")}
                      {...renderConditionalProp(
                        buttonClick?.scheduleFlag || disabled ? true : false,
                        "disabled",
                      )}
                      onClick={
                        buttonClick?.scheduleFlag || disabled
                          ? null
                          : ack.valueCheckedHandler
                      }
                    ></ft-checkbox>
                  </ft-formfield>
                )}
                <p
                  className={classes.Confirm}
                  style={{ display: disabled ? "none" : "block" }}
                >
                  {action === PaymentActionType.SAVE && ack.value !== true
                    ? t("payment_next:ach_save_confirm")
                    : ""}
                </p>
              </>
            )}
            {action === PaymentActionType.SAVE ? (
              <div className={classes.FormButtons}>
                <ft-button
                  class={[classes.Back, "outline-button"].join(" ")}
                  data-testid="add-card-payment-method-back"
                  label={t("common:back_label")}
                  outlined
                  onClick={() => {
                    dispatch(updateSlide({ id: "ManageWallet" }));
                  }}
                ></ft-button>
                <ft-button
                  class={classes.Create}
                  data-testid="add-card-payment-method-save"
                  label={editMode ? t("common:update") : t("common:save")}
                  unelevated
                  {...renderConditionalProp(ack.value !== true, "disabled")}
                  onClick={submithandler}
                ></ft-button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);
